import type { ProductCapabilitiesMap, ProductCapabilityItem } from './kieu';

/**
 * Authoritative Product Capability Keys
 *
 * Mapped 1:1 to lab-server/schemas/actionCatalog.schema.js (PRODUCT_CAPABILITY_MAP)
 * and GET /api/fbi/product-capabilities.
 * LAW: UI-04, ARCH-01, SEC-01.
 */
export const CAPABILITY_KEYS = {
  POST_GROUP_TEXT: 'POST_GROUP_TEXT',
  POST_GROUP_PHOTO: 'POST_GROUP_PHOTO',
  POST_GROUP_VIDEO: 'POST_GROUP_VIDEO',
  POST_GROUP_BACKGROUND: 'POST_GROUP_BACKGROUND',
  CREATE_COMMENT: 'CREATE_COMMENT',
  FIRST_COMMENT: 'FIRST_COMMENT',
  RECURRING_SCHEDULE: 'RECURRING_SCHEDULE',
  RECURRING: 'RECURRING',
  POST_PROFILE_TEXT: 'POST_PROFILE_TEXT',
  POST_GROUP_ANON_TEXT: 'POST_GROUP_ANON_TEXT',
  POST_GROUP_CROSSPOST: 'POST_GROUP_CROSSPOST',
  POST_PAGE_TEXT: 'POST_PAGE_TEXT',
  POST_PROFILE_REEL: 'POST_PROFILE_REEL',
  POST_PROFILE_BACKGROUND: 'POST_PROFILE_BACKGROUND',
  POST_PROFILE_PHOTO: 'POST_PROFILE_PHOTO',
  POST_PROFILE_VIDEO: 'POST_PROFILE_VIDEO',
  POST_GROUP_ANON_PHOTO: 'POST_GROUP_ANON_PHOTO',
} as const;

export type CapabilityKey = keyof typeof CAPABILITY_KEYS;

/**
 * Checks whether a specific capability is enabled on the server.
 */
export function isCapabilityEnabled(
  capabilities: ProductCapabilitiesMap | null | undefined,
  key: string
): boolean {
  if (!capabilities || !capabilities[key]) return false;
  return Boolean(capabilities[key].enabled);
}

/**
 * Checks whether a capability is marked planned (coming soon / upsell preview).
 */
export function isCapabilityPlanned(
  capabilities: ProductCapabilitiesMap | null | undefined,
  key: string
): boolean {
  if (!capabilities || !capabilities[key]) return false;
  return Boolean(capabilities[key].planned);
}

/**
 * Retrieves the authoritative reason string for a capability.
 */
export function getCapabilityReason(
  capabilities: ProductCapabilitiesMap | null | undefined,
  key: string,
  fallback = 'Tính năng chưa mở trong phiên bản MVP.'
): string {
  if (!capabilities || !capabilities[key] || typeof capabilities[key].reason !== 'string') {
    return fallback;
  }
  return capabilities[key].reason;
}

/**
 * Formats a user-facing explanation label for planned or disabled actions.
 * Never guesses platform conditions; presents server-provided reason.
 */
export function formatPlannedActionNotice(
  capabilities: ProductCapabilitiesMap | null | undefined,
  key: string,
  fallback = 'Tính năng chưa mở trong phiên bản MVP.'
): string {
  const reason = getCapabilityReason(capabilities, key, fallback);
  return reason.startsWith('Chưa mở') ? reason : `Chưa mở: ${reason}`;
}

export interface MediaCapabilityStatus {
  photoEnabled: boolean;
  videoEnabled: boolean;
  photoReason: string;
  videoReason: string;
  acceptedMimeTypes: string;
  canAttachMedia: boolean;
  unsupportedMediaReason: string | null;
}

/**
 * Determines granular upload media capabilities based on server authority.
 */
export function getMediaCapabilityStatus(
  capabilities: ProductCapabilitiesMap | null | undefined
): MediaCapabilityStatus {
  const photoEnabled = isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_PHOTO);
  const videoEnabled = isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_VIDEO);
  const photoReason = getCapabilityReason(
    capabilities,
    CAPABILITY_KEYS.POST_GROUP_PHOTO,
    'Tính năng đăng ảnh tạm thời chưa mở.'
  );
  const videoReason = getCapabilityReason(
    capabilities,
    CAPABILITY_KEYS.POST_GROUP_VIDEO,
    'Tính năng đăng video tạm thời chưa mở.'
  );

  let acceptedMimeTypes = '';
  if (photoEnabled && videoEnabled) {
    acceptedMimeTypes = 'image/jpeg,image/png,image/webp,video/mp4';
  } else if (photoEnabled) {
    acceptedMimeTypes = 'image/jpeg,image/png,image/webp';
  } else if (videoEnabled) {
    acceptedMimeTypes = 'video/mp4';
  }

  const canAttachMedia = photoEnabled || videoEnabled;
  let unsupportedMediaReason: string | null = null;
  if (!canAttachMedia) {
    unsupportedMediaReason = 'Tính năng đính kèm ảnh và video tạm thời chưa sẵn sàng.';
  }

  return {
    photoEnabled,
    videoEnabled,
    photoReason,
    videoReason,
    acceptedMimeTypes,
    canAttachMedia,
    unsupportedMediaReason,
  };
}

export interface SanitizedDraftResult<T> {
  sanitized: T;
  notices: string[];
}

/**
 * Clears unsupported legacy modes from drafts while strictly preserving user content.
 * Prevents accidental misrouting or silent dispatch to groups instead of intended target.
 */
export function sanitizeDraftWithCapabilities<T extends {
  postToProfile?: boolean;
  anonymous?: boolean;
  draftCrossPostEnabled?: boolean;
}>(
  draft: T,
  capabilities: ProductCapabilitiesMap | null | undefined
): SanitizedDraftResult<T> {
  const notices: string[] = [];
  // Preserve blocked intent instead of silently clearing or auto-routing to groups (UI-04 / DATA-02)
  const sanitized = { ...draft };

  // Profile posting unsupported
  if (draft.postToProfile && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_PROFILE_TEXT)) {
    notices.push(
      'Bản nháp đang chọn Đăng lên Trang cá nhân (chưa mở trong MVP). Ý định được giữ nguyên và khóa nút gửi cho đến khi bạn xác nhận thay đổi để tránh gửi nhầm nhóm.'
    );
  }

  // Anonymous posting unsupported
  if (draft.anonymous && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_ANON_TEXT)) {
    notices.push(
      'Bản nháp đang bật chế độ Đăng ẩn danh (Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này). Ý định được giữ nguyên và khóa nút gửi cho đến khi bạn tắt ẩn danh.'
    );
  }

  // Crossposting unsupported
  if (draft.draftCrossPostEnabled && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_CROSSPOST)) {
    notices.push(
      'Bản nháp đang bật chế độ Đăng chéo (tạm khóa trong MVP). Ý định được giữ nguyên và khóa nút gửi cho đến khi bạn tắt đăng chéo.'
    );
  }

  return { sanitized, notices };
}

export interface SubmitCapabilityCheckInput {
  capabilities: ProductCapabilitiesMap | null | undefined;
  isLoaded: boolean;
  loadError: string | null;
  isUnpaid?: boolean;
  isPage: boolean;
  postToProfile: boolean;
  anonymousIntent?: boolean;
  isCrossPost: boolean;
  backgroundId: string | null;
  mediaMimeTypes?: string[];
  enableFirstComment: boolean;
  hasSchedule: boolean;
}

/**
 * Validates post submission intent against server capabilities.
 * Enforces fail-closed submit without restricting navigation.
 */
export function validateSubmitWithCapabilities(
  input: SubmitCapabilityCheckInput
): { ok: boolean; reason: string | null } {
  if (input.isUnpaid) {
    return {
      ok: false,
      reason: 'Cần kích hoạt hoặc gia hạn gói dịch vụ để thực hiện gửi bài.',
    };
  }

  // Fail-closed submit if capabilities are unavailable or in error
  if (!input.isLoaded || input.loadError || !input.capabilities) {
    return {
      ok: false,
      reason: 'Không thể xác thực tính năng với máy chủ. Vui lòng tải lại hoặc kiểm tra kết nối.',
    };
  }

  const { capabilities } = input;

  // 1. Page posting
  if (input.isPage && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_PAGE_TEXT)) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.POST_PAGE_TEXT,
        'Tính năng đăng bài với tư cách Fanpage tạm khóa trong phiên bản MVP; tài khoản Trang chỉ hỗ trợ xem danh sách.'
      ),
    };
  }

  // 2. Profile posting
  if (input.postToProfile && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_PROFILE_TEXT)) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.POST_PROFILE_TEXT,
        'Tính năng đăng lên dòng thời gian cá nhân (Profile Feed) tạm khóa trong phiên bản MVP chờ kiểm chứng an toàn.'
      ),
    };
  }

  // 3. Anonymous posting
  if (input.anonymousIntent && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_ANON_TEXT)) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.POST_GROUP_ANON_TEXT,
        'Chưa hoàn tất nghiệm thu chế độ ẩn danh trong bản phát hành này.'
      ),
    };
  }

  // 4. Cross-posting
  if (input.isCrossPost && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_CROSSPOST)) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.POST_GROUP_CROSSPOST,
        'Tính năng đăng chéo tạm khóa trong phiên bản MVP chờ kiểm chứng đối soát phân phối nhóm phụ.'
      ),
    };
  }

  // 5. Background post
  if (input.backgroundId && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_BACKGROUND)) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.POST_GROUP_BACKGROUND,
        'Tính năng đăng bài phông nền tạm thời chưa sẵn sàng.'
      ),
    };
  }

  // 6. Media mime types
  if (Array.isArray(input.mediaMimeTypes) && input.mediaMimeTypes.length > 0) {
    const hasVideo = input.mediaMimeTypes.some((m) => m === 'video/mp4');
    const hasPhoto = input.mediaMimeTypes.some((m) => m.startsWith('image/'));

    if (hasVideo && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_VIDEO)) {
      return {
        ok: false,
        reason: getCapabilityReason(
          capabilities,
          CAPABILITY_KEYS.POST_GROUP_VIDEO,
          'Tính năng đăng video tạm thời chưa mở.'
        ),
      };
    }

    if (hasPhoto && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.POST_GROUP_PHOTO)) {
      return {
        ok: false,
        reason: getCapabilityReason(
          capabilities,
          CAPABILITY_KEYS.POST_GROUP_PHOTO,
          'Tính năng đăng ảnh tạm thời chưa mở.'
        ),
      };
    }
  }

  // 7. First comment
  if (input.enableFirstComment && !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.FIRST_COMMENT)) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.FIRST_COMMENT,
        'Tính năng bình luận đầu bài viết tạm thời chưa mở.'
      ),
    };
  }

  // 8. Recurring or scheduled post
  if (
    input.hasSchedule &&
    !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.RECURRING_SCHEDULE) &&
    !isCapabilityEnabled(capabilities, CAPABILITY_KEYS.RECURRING)
  ) {
    return {
      ok: false,
      reason: getCapabilityReason(
        capabilities,
        CAPABILITY_KEYS.RECURRING_SCHEDULE,
        'Tính năng lên lịch định kỳ tạm thời chưa mở.'
      ),
    };
  }

  return { ok: true, reason: null };
}

export type { ProductCapabilitiesMap, ProductCapabilityItem };
